import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tooltip,
} from "@mui/material";

const SimplesNacionalConfig = ({ onAlert, onConfigChange, initialData }) => {
  const [configs, setConfigs] = useState({
    // Apenas as configurações que podem ser editadas
    icmsPercentage: "3",
    pisCofinsPercentage: "4",
    // Dados do backend
    icmsAliquota: "",
    icmsRegimeSimples: "",
    pisCofinsRegimeSimples: "",
  });

  // Efeito para carregar dados do backend sempre que mudarem
  useEffect(() => {
    if (initialData) {
      const savedConfig = localStorage.getItem(
        "tax_regime_config_simples_nacional"
      );
      const localConfig = savedConfig ? JSON.parse(savedConfig) : {};
      setConfigs((prev) => ({
        ...prev,
        ...localConfig,
        // Dados do backend têm prioridade
        icmsAliquota: initialData.icmsAliquota || "",
        icmsRegimeSimples: initialData.icmsRegimeSimples || "",
        pisCofinsRegimeSimples: initialData.pisCofinsRegimeSimples || "",
        // Usar os valores do backend como padrão se existirem
        icmsPercentage:
          initialData.icmsRegimeSimples || localConfig.icmsPercentage || "3",
        pisCofinsPercentage:
          initialData.pisCofinsRegimeSimples ||
          localConfig.pisCofinsPercentage ||
          "4",
      }));
    }
  }, [initialData]);

  const anexos = [
    {
      value: "I",
      label: "Anexo I - Comércio",
      description: "Atividades de comércio",
    },
    {
      value: "II",
      label: "Anexo II - Indústria",
      description: "Atividades industriais",
    },
    {
      value: "III",
      label: "Anexo III - Serviços",
      description: "Serviços em geral",
    },
    {
      value: "IV",
      label: "Anexo IV - Serviços",
      description: "Serviços específicos",
    },
    {
      value: "V",
      label: "Anexo V - Serviços",
      description: "Serviços profissionais",
    },
  ];

  // Função para registrar cliques no localStorage (robusta)
  const handleFieldClick = (field) => {
    try {
      const key = `simplesNacionalConfig_clicks_${field}`;
      let count = 0;
      const stored = window.localStorage.getItem(key);
      if (stored !== null && !isNaN(Number(stored))) {
        count = Number(stored);
      }
      window.localStorage.setItem(key, String(count + 1));
    } catch (e) {}
  };
  const handleConfigChange = (field, value) => {
    const newConfigs = { ...configs, [field]: value };
    setConfigs(newConfigs);
    localStorage.setItem(
      "tax_regime_config_simples_nacional",
      JSON.stringify(newConfigs)
    );
    // Notifica o componente pai sobre a alteração
    onConfigChange && onConfigChange();
    onAlert && onAlert("Configurações atualizadas com sucesso", "success");
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Card
        sx={{
          border: "1px solid #e8eaed",
          borderRadius: 3,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Cabeçalho */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 3,
              color: "#2e7d32",
              display: "flex",
              alignItems: "center",
              gap: 2,
              fontSize: "1.2rem",
              borderBottom: "2px solid #e8f5e8",
              paddingBottom: 2,
            }}
          >
            ⚙️ Configurações do Simples Nacional
          </Typography>

          {/* Seção não editável - apenas informativa */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 3,
              mb: 3,
            }}
          >
            <Box
              sx={{
                flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" },
                minWidth: "250px",
              }}
            >
              <TextField
                fullWidth
                label="ICMS Alíquota (%)"
                value={configs.icmsAliquota || ""}
                onChange={() => {}}
                type="number"
                size="small"
                helperText="Alíquota do ICMS configurada"
                disabled
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f8f9fa",
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" },
                minWidth: "250px",
              }}
            >
              <Tooltip title="Em breve" arrow>
                <span>
                  <TextField
                    fullWidth
                    label="Faturamento Anual (R$)"
                    value={configs.annualRevenue}
                    onChange={() => {}}
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    size="small"
                    helperText="Receita bruta dos últimos 12 meses"
                    FormHelperTextProps={{
                      sx: {
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#666",
                        mt: 0.5,
                      },
                    }}
                    disabled
                    onClick={() => handleFieldClick("annualRevenue")}
                  />
                </span>
              </Tooltip>
            </Box>

            <Box
              sx={{
                flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" },
                minWidth: "250px",
              }}
            >
              <Tooltip title="Em breve" arrow>
                <span>
                  <FormControl fullWidth size="small" disabled>
                    <InputLabel>Anexo</InputLabel>
                    <Select
                      value={configs.anexo}
                      label="Anexo"
                      onChange={() => {}}
                      onClick={() => handleFieldClick("anexo")}
                    >
                      {anexos.map((anexo) => (
                        <MenuItem key={anexo.value} value={anexo.value}>
                          {anexo.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </span>
              </Tooltip>
            </Box>

            <Box
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 12px)",
                  md: "1 1 calc(25% - 18px)",
                },
                minWidth: "180px",
              }}
            >
              <Tooltip title="Em breve" arrow>
                <span>
                  <TextField
                    fullWidth
                    label="Alíquota (%)"
                    value={configs.aliquota}
                    onChange={() => {}}
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    size="small"
                    helperText="Calculada automaticamente"
                    FormHelperTextProps={{
                      sx: {
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#666",
                        mt: 0.5,
                      },
                    }}
                    disabled
                    onClick={() => handleFieldClick("aliquota")}
                  />
                </span>
              </Tooltip>
            </Box>

            <Box
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 12px)",
                  md: "1 1 calc(25% - 18px)",
                },
                minWidth: "180px",
              }}
            >
              <Tooltip title="Em breve" arrow>
                <span>
                  <TextField
                    fullWidth
                    label="ISEP (%)"
                    value={configs.isep}
                    onChange={() => {}}
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    size="small"
                    helperText="Imposto Seletivo"
                    FormHelperTextProps={{
                      sx: {
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#666",
                        mt: 0.5,
                      },
                    }}
                    disabled
                    onClick={() => handleFieldClick("isep")}
                  />
                </span>
              </Tooltip>
            </Box>

            <Box
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 12px)",
                  md: "1 1 calc(25% - 18px)",
                },
                minWidth: "180px",
              }}
            >
              <Tooltip title="Em breve" arrow>
                <span>
                  <TextField
                    fullWidth
                    label="Dedução R$"
                    value={configs.deductionR}
                    onChange={() => {}}
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    size="small"
                    helperText="Valor a deduzir"
                    FormHelperTextProps={{
                      sx: {
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#666",
                        mt: 0.5,
                      },
                    }}
                    disabled
                    onClick={() => handleFieldClick("deductionR")}
                  />
                </span>
              </Tooltip>
            </Box>

            <Box
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 12px)",
                  md: "1 1 calc(25% - 18px)",
                },
                minWidth: "180px",
              }}
            >
              <Tooltip title="Em breve" arrow>
                <span>
                  <TextField
                    fullWidth
                    label="Adicional (%)"
                    value={configs.additionalPercentage}
                    onChange={() => {}}
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    size="small"
                    helperText="Percentual adicional"
                    FormHelperTextProps={{
                      sx: {
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#666",
                        mt: 0.5,
                      },
                    }}
                    disabled
                    onClick={() => handleFieldClick("additionalPercentage")}
                  />
                </span>
              </Tooltip>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Campos editáveis para regras do Simples Nacional */}
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Seção ICMS do Produto */}
            <Box
              sx={{
                p: 3,
                bgcolor: "#e8f5e8",
                borderRadius: 2,
                border: "1px solid #c8e6c9",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: "#1a1a1a",
                  fontSize: "1rem",
                }}
              >
                ICMS do Produto
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  fullWidth
                  label="% ICMS sobre a venda"
                  type="number"
                  size="small"
                  value={configs.icmsPercentage}
                  onChange={(e) =>
                    handleConfigChange("icmsPercentage", e.target.value)
                  }
                  placeholder="Ex: 3"
                  InputProps={{
                    endAdornment: (
                      <Typography variant="caption" color="text.secondary">
                        %
                      </Typography>
                    ),
                  }}
                  helperText="Porcentagem única de ICMS aplicada sobre todas as vendas"
                  FormHelperTextProps={{
                    sx: {
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: "#666",
                      mt: 0.5,
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "#4caf50",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4caf50",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#4caf50",
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Seção PIS/COFINS */}
            <Box
              sx={{
                p: 3,
                bgcolor: "#fff8e1",
                borderRadius: 2,
                border: "1px solid #ffcc80",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: "#1a1a1a",
                  fontSize: "1rem",
                }}
              >
                PIS/COFINS
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  fullWidth
                  label="% PIS/COFINS sobre a venda"
                  type="number"
                  size="small"
                  value={configs.pisCofinsPercentage}
                  onChange={(e) =>
                    handleConfigChange("pisCofinsPercentage", e.target.value)
                  }
                  placeholder="Ex: 4"
                  InputProps={{
                    endAdornment: (
                      <Typography variant="caption" color="text.secondary">
                        %
                      </Typography>
                    ),
                  }}
                  helperText="Porcentagem única de PIS/COFINS aplicada sobre todas as vendas"
                  FormHelperTextProps={{
                    sx: {
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: "#666",
                      mt: 0.5,
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "#4caf50",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4caf50",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#4caf50",
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SimplesNacionalConfig;
